import React, { useMemo, useState } from "react";
import { alpha, Button, IconButton, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import {
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
} from "@mui/icons-material";
import { OrderDirection } from "~/components/ui-library";
import { useSort } from "~/pages/inventory/hooks/useSort";
import { Check as CheckIcon } from "@mui/icons-material";
import { Header } from "~/components/FirewatchPage/FirewatchPageColumns";
import { FilterMenuDropdown } from "~/components/Filter/FilterMenu/FilterMenuDropdown/FilterMenuDropdown";
import { FilterMenuList } from "~/components/Filter/FilterMenu/FilterMenuList/FilterMenuList";
import { FilterMenuItem } from "~/components/Filter/FilterMenu/FilterMenuItem/FilterMenuItem";
import { Header as FindingTableHeader } from "~/types/table";

interface FindingsTableSortProps {
  sort?: ReturnType<typeof useSort>;
  sortableHeaders: Header[] | FindingTableHeader[];
}

export const FindingsTableSort = ({
  sort,
  sortableHeaders = [],
}: FindingsTableSortProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const selectedHeaderLabel = useMemo(
    () =>
      sortableHeaders.find(
        (header) =>
          (header as Header).field === sort?.orderBy.field ||
          (header as FindingTableHeader).id === sort?.orderBy.field,
      )?.label || "",
    [sortableHeaders, sort?.orderBy.field],
  );

  const menuItems = sortableHeaders.map((item) => {
    const field = (item as Header).field;
    return (
      <FilterMenuItem
        autoFocus
        key={field || item.id}
        label={item.label as string}
        value={field || item.id}
        component={RouterLink}
        to={sort?.sortColumnUrl(field || "")?.href || "#"}
        role="menuitemradio"
        aria-checked={field === sort?.orderBy.field}
        selected={field === sort?.orderBy.field}
        sx={{
          height: 40,
          backgroundColor: "background.default",
          "&.Mui-selected": {
            backgroundColor: "background.default",
            "&:hover": {
              backgroundColor: "background.paper",
            },
          },
        }}
      >
        <Typography
          sx={{
            color:
              field === sort?.orderBy.field ? "text.disabled" : "text.primary",
            textDecoration: "none",
            "&:focus": { outline: "none" },
            "&:focus-visible": { outline: "none" },
          }}
          component={RouterLink}
          to={sort?.sortColumnUrl(field || "")?.href || "#"}
        >
          {item.label}
        </Typography>

        {field === sort?.orderBy.field && (
          <Typography color="primary" sx={{ ml: 1, mt: 0.7 }}>
            <CheckIcon />
          </Typography>
        )}
      </FilterMenuItem>
    );
  });

  return (
    <div>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Button
          onClick={handleOpen}
          aria-haspopup="listbox"
          aria-expanded={isMenuOpen ? "true" : undefined}
          sx={{
            backgroundColor: (theme) =>
              isMenuOpen
                ? alpha(theme.palette.primary.main, 0.2)
                : "transparent",
            "&:hover": {
              backgroundColor: (theme) =>
                isMenuOpen
                  ? alpha(theme.palette.primary.main, 0.2)
                  : "background.paper",
            },
            border: isMenuOpen
              ? (theme) => `1px solid ${theme.palette.primary.main}`
              : "1px solid transparent",
            borderBottom: "none",
            borderBottomLeftRadius: isMenuOpen ? 0 : 4,
            borderBottomRightRadius: isMenuOpen ? 0 : 4,
          }}
        >
          <Typography sx={{ color: "text.secondary", textTransform: "none" }}>
            Sorted by
          </Typography>
          <Typography
            sx={{
              ml: 1,
              textTransform: "capitalize",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            {selectedHeaderLabel}
          </Typography>
        </Button>

        <IconButton
          component={RouterLink}
          to={sort?.sortColumnUrl(sort?.orderBy.field || "")?.href || "#"}
          size="small"
          sx={{
            color: "primary.light",
            ml: "0 !important",
            p: 0.5,
            "&:hover": { backgroundColor: "background.paper" },
          }}
        >
          {sort?.orderBy.direction === OrderDirection.Desc ? (
            <ArrowDownwardIcon fontSize="small" />
          ) : (
            <ArrowUpwardIcon fontSize="small" />
          )}
        </IconButton>
      </Stack>

      <FilterMenuDropdown
        showKeyboardHint
        open={isMenuOpen}
        onClose={handleClose}
        anchorEl={anchorEl}
        placement="bottom-end"
        children={
          <FilterMenuList
            sx={{ maxHeight: "400px", m: 0, p: 2, pt: 2 }}
            onClick={handleClose}
          >
            {menuItems}
          </FilterMenuList>
        }
      />
    </div>
  );
};
